// assets/js/layouts/menuRenderer.js

import { menuConfig } from './menu.js';
import { authService } from '../api/AuthService.js';

/**
 * Controlla se un utente ha il permesso di vedere una voce di menu.
 * @param {number} userRole L'ID del ruolo dell'utente.
 * @param {Array<number>|undefined} allowedRoles I ruoli permessi per la voce.
 * @returns {boolean}
 */
const hasPermission = (userRole, allowedRoles) => {
    if (!allowedRoles) {
        return true;
    }
    return allowedRoles.includes(userRole);
};

/**
 * Funzione ricorsiva per generare l'HTML di una singola voce di menu e dei suoi figli.
 * @param {object} item L'oggetto della voce di menu dalla configurazione.
 * @param {number} userRole L'ID del ruolo dell'utente.
 * @returns {string} L'HTML generato.
 */
const buildMenuItem = (item, userRole) => {
    if (!hasPermission(userRole, item.roles)) {
        return '';
    }
    switch (item.type) {
        case 'separator':
            return '<div class="border-b border-input mt-4 mb-1 mx-3.5"></div>';
        case 'heading':
            return `<div class="px-3.5 pt-4 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">${item.title}</div>`;
    }
    const currentPath = window.location.pathname;
    const hasChildren = item.children && item.children.length > 0;

    // --- INIZIO CORREZIONE ---
    // Determina se la voce di menu è attiva (o uno dei suoi figli è attivo)
    const isActive = hasChildren
        ? item.children.some(child => child.path === currentPath || (child.children && child.children.some(subChild => subChild.path === currentPath)))
        : currentPath === item.path;
    // --- FINE CORREZIONE ---

    // Gestisce i link diretti all'interno di un sottomenu (es. Statistiche)
    if (item.isDirectLink) {
        return `
            <div class="kt-menu-item">
                <a class="kt-menu-link py-2 px-2.5 rounded-md border border-transparent ${currentPath === item.path ? 'kt-menu-item-active' : ''}" href="${item.path}">
                    <span class="kt-menu-title text-sm text-foreground kt-menu-item-active:text-mono">${item.title}</span>
                </a>
            </div>
        `;
    }

    const isAccordion = hasChildren ? 'data-kt-menu-item-toggle="accordion" data-kt-menu-item-trigger="click"' : '';
    const linkClass = item.icon ? 'gap-2.5 py-2 px-2.5' : 'py-2 px-2.5';

    let childrenHtml = '';
    if (hasChildren) {
        const subMenuClass = item.icon ? 'ps-7' : 'ps-2.5';
        childrenHtml = `
            <div class="kt-menu-accordion gap-px ${subMenuClass}">
                ${item.children.map(child => buildMenuItem(child, userRole)).join('')}
            </div>
        `;
    }

    // --- INIZIO CORREZIONE STRUTTURALE ---
    // Usa un <div> per le voci con figli e un <a> per i link diretti.
    const linkOrDivTag = hasChildren ? 'div' : 'a';
    const hrefAttribute = hasChildren ? '' : `href="${item.path}"`;
    // --- FINE CORREZIONE STRUTTURALE ---

    return `
        <div class="kt-menu-item ${isActive && hasChildren ? 'here' : ''}" ${isAccordion}>
            <${linkOrDivTag} class="kt-menu-link rounded-md border border-transparent ${linkClass} ${currentPath === item.path ? 'kt-menu-item-active' : ''}" ${hrefAttribute}>
                ${item.icon ? `
                    <span class="kt-menu-icon items-start text-lg text-secondary-foreground kt-menu-item-active:text-foreground kt-menu-item-here:text-foreground">
                        <i class="ki-filled ${item.icon}"></i>
                    </span>` : ''
    }
                <span class="kt-menu-title text-sm font-medium text-foreground kt-menu-item-here:text-mono">
                    ${item.title}
                </span>
                ${hasChildren ? `
                    <span class="kt-menu-arrow text-muted-foreground kt-menu-item-show:text-foreground">
                        <span class="inline-flex kt-menu-item-show:hidden"><i class="ki-filled ki-down text-xs"></i></span>
                        <span class="hidden kt-menu-item-show:inline-flex"><i class="ki-filled ki-up text-xs"></i></span>
                    </span>` : ''
    }
            </${linkOrDivTag}>
            ${childrenHtml}
        </div>
    `;
};


/**
 * Renderizza l'intero menu nella sidebar.
 */
export const renderMenu = () => {
    const menuContainer = document.getElementById('sidebar_primary_menu');
    if (!menuContainer) {
        console.error("Contenitore del menu '#sidebar_primary_menu' non trovato.");
        return;
    }

    const user = authService.currentUser;
    if (!user || typeof user.idRuolo === 'undefined') {
        console.error("Dati utente o ruolo non disponibili per renderizzare il menu.");
        menuContainer.innerHTML = '';
        return;
    }

    const searchContainer = document.getElementById('sidebar_search_container');
    if (searchContainer) {
        // Il campo parte nascosto da HTML. Lo mostriamo solo per i ruoli 1 e 2.
        if (user.idRuolo === 1 || user.idRuolo === 2) {
            searchContainer.classList.remove('hidden');
        } else {
            // Per tutti gli altri ruoli (es. 3), lo eliminiamo dal DOM
            searchContainer.remove();
        }
    }

    const userRole = user.idRuolo;
    const menuHtml = menuConfig.map(item => buildMenuItem(item, userRole)).join('');
    menuContainer.innerHTML = menuHtml;

    // Approccio "Distruggi e Ricrea" per massima affidabilità
    setTimeout(() => {
        const menuElement = document.querySelector('#sidebar_primary_menu');
        if (menuElement && typeof KTMenu !== 'undefined') {
            const existingInstance = KTMenu.getInstance(menuElement);
            if (existingInstance) {
                existingInstance.dispose();
            }
            new KTMenu(menuElement);
            //console.log("Istanza KTMenu ricreata con la struttura HTML corretta.");
        }
    }, 0);
};